/**
 * RouterComponent.js - Functional; route structure matching reference.
 * Uses react-router-dom v6: Routes, Route, Navigate.
 * Navigation is tree-based in left drawer only (no tabs in content).
 */
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getCachevalue } from "./Services/CacheProviderService";
import { getScreenAccess, getFirstAllowedRoute, canAccessPath } from "./Services/RoleAccessService";
import Signin from "../Signin/Signin";
import {
  HistopathologyConfigHome,
  CorporateIndividualPayment,
} from "./HistopathologyTabs";
import NewBill from "../HistoModule/Home/NewBill";
import ExistingBill from "../HistoModule/Home/ExistingBill";
import HistopathologyReport from "../HistoModule/Home/HistopathologyReport";
import Demographics from "../HistoModule/Home/Demographics";
import HistopathologyQuickBilling from "../HistoModule/Home/HistopathologyQuickBilling";
import HospitalBilling from "../HistoModule/Billing/HospitalBilling";
import CashCounter from "../HistoModule/Billing/CashCounter";
import SpecimenCollection from "../HistoModule/Specimen/SpecimenCollection";
import OutsourceTest from "../HistoModule/Specimen/OutsourceTest";
import PendingList from "../HistoModule/Reporting/PendingList";
import ApprovalList from "../HistoModule/Reporting/ApprovalList";
import CompletedList from "../HistoModule/Reporting/CompletedList";
import CorporateProvider from "../HistoModule/CreditSettlement/CorporateProvider";
import SettlementReport from "../HistoModule/CreditSettlement/SettlementReport";
import OverallBills from "../HistoModule/BillingTransaction/OverallBills";
import CreditBills from "../HistoModule/BillingTransaction/CreditBills";
import ReceiptBills from "../HistoModule/BillingTransaction/ReceiptBills";
import BillReport from "../HistoModule/Reports/BillReport";
import PatientDemographicsReport from "../HistoModule/Reports/PatientDemographicsReport";
import OutsourceReport from "../HistoModule/Reports/OutsourceReport";
import TATReport from "../HistoModule/Reports/TATReport";
import ServiceMaster from "../HistoModule/Masters/ServiceMaster";
import ResultTemplate from "../HistoModule/Masters/ResultTemplate";
import SpecimenMaster from "../HistoModule/Masters/SpecimenMaster";
import AddResultTemplate from "../HistoModule/Masters/AddResultTemplate";
import DemographicsLayout from "../HistoModule/Masters/DemographicsLayout";
import OutSourceConfiguration from "../HistoModule/Masters/OutSourceConfiguration";
import ESignConfiguration from "../HistoModule/Masters/eSignConfiguration";
import ReferredDoctor from "../HistoModule/Masters/ReferredDoctor";
import CustomConfiguration from "../HistoModule/Masters/CustomConfiguration";
import CorporateConfiguration from "../HistoModule/Masters/CorporateConfiguration";
import RoleAccess from "../HistoModule/Masters/UserConfiguration/RoleAccess";
import UserConfig from "../HistoModule/Masters/UserConfiguration/UserConfig";
import AddRoleAccess from "../HistoModule/Masters/UserConfiguration/AddRoleAccess";

// Placeholder pages – histopathology-only project; no other modules
const ForgotPassword = () => <div>Forgot Password</div>;
const ResetPassword = () => <div>Reset Password</div>;
const UnlockAccount = () => <div>Unlock Account</div>;
const PageNotFound = () => <div>404 Not Found</div>;

function ProtectedRoute({ children }) {
  const isAuthenticated = getCachevalue("isLoggedIn");
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!canAccessPath(location.pathname)) {
    const fallback = getFirstAllowedRoute(getScreenAccess());
    if (fallback && fallback !== location.pathname) {
      return <Navigate to={fallback} replace />;
    }
  }
  return children;
}

function PublicRoute({ children }) {
  const isAuthenticated = getCachevalue("isLoggedIn");
  if (isAuthenticated) {
    const firstRoute = getFirstAllowedRoute(getScreenAccess());
    return <Navigate to={firstRoute} replace />;
  }
  return children;
}

export default function RouterComponent() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><Signin /></PublicRoute>} />
      <Route path="/ForgotPassword" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/ResetPassword" element={<ResetPassword />} />
      <Route path="/UnlockAccount" element={<UnlockAccount />} />

      {/* Histopathology routes – tree menu in left drawer; no tabs in content */}
      <Route path="/HistopathologyHome" element={<ProtectedRoute><NewBill /></ProtectedRoute>} />
      <Route path="/HistopathologyHome/NewBill" element={<ProtectedRoute><NewBill /></ProtectedRoute>} />
      <Route path="/HistopathologyHome/ExistingBill" element={<ProtectedRoute><ExistingBill /></ProtectedRoute>} />
      <Route path="/HistopathologyHome/HistopathologyReport" element={<ProtectedRoute><HistopathologyReport /></ProtectedRoute>} />
      <Route path="/HistopathologyHome/Demographics" element={<ProtectedRoute><Demographics /></ProtectedRoute>} />
      <Route path="/HistopathologyHome/HistopathologyQuickBilling" element={<ProtectedRoute><HistopathologyQuickBilling /></ProtectedRoute>} />
      <Route path="/HistopathologyBilling/HospitalBilling" element={<ProtectedRoute><HospitalBilling /></ProtectedRoute>} />
      <Route path="/HistopathologyBilling/CashCounter" element={<ProtectedRoute><CashCounter /></ProtectedRoute>} />
      <Route path="/HistopathologySpecimen/SpecimenCollection" element={<ProtectedRoute><SpecimenCollection /></ProtectedRoute>} />
      <Route path="/HistopathologySpecimen/OutsourceTest" element={<ProtectedRoute><OutsourceTest /></ProtectedRoute>} />
      <Route path="/HistopathologyReporting/PendingList" element={<ProtectedRoute><PendingList /></ProtectedRoute>} />
      <Route path="/HistopathologyReporting/ApprovalList" element={<ProtectedRoute><ApprovalList /></ProtectedRoute>} />
      <Route path="/HistopathologyReporting/CompletedList" element={<ProtectedRoute><CompletedList /></ProtectedRoute>} />
      <Route path="/HistopathologyBillingTransaction/OverallBills" element={<ProtectedRoute><OverallBills /></ProtectedRoute>} />
      <Route path="/HistopathologyBillingTransaction/CreditBills" element={<ProtectedRoute><CreditBills /></ProtectedRoute>} />
      <Route path="/HistopathologyBillingTransaction/ReceiptBills" element={<ProtectedRoute><ReceiptBills /></ProtectedRoute>} />
      <Route path="/HistopathologyBillingTransaction" element={<Navigate to="/HistopathologyBillingTransaction/OverallBills" replace />} />
      <Route path="/HistopathologyCreditSettlement/CorporateProvider" element={<ProtectedRoute><CorporateProvider /></ProtectedRoute>} />
      <Route path="/HistopathologyCreditSettlement/SettlementReport" element={<ProtectedRoute><SettlementReport /></ProtectedRoute>} />
      <Route path="/HistopathologyCreditSettlement/Corporate/Payment" element={<ProtectedRoute><CorporateIndividualPayment /></ProtectedRoute>} />
      <Route path="/HistopathologyReports/BillReport" element={<ProtectedRoute><BillReport /></ProtectedRoute>} />
      <Route path="/HistopathologyReports/PatientDemographicsReport" element={<ProtectedRoute><PatientDemographicsReport /></ProtectedRoute>} />
      <Route path="/HistopathologyReports/OutsourceReport" element={<ProtectedRoute><OutsourceReport /></ProtectedRoute>} />
      <Route path="/HistopathologyReports/TATReport" element={<ProtectedRoute><TATReport /></ProtectedRoute>} />
      <Route path="/HistopathologySetting" element={<ProtectedRoute><HistopathologyConfigHome /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/MasterConfiguration/ServiceMaster" element={<ProtectedRoute><ServiceMaster /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/MasterConfiguration/ResultTemplate" element={<ProtectedRoute><ResultTemplate /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/MasterConfiguration/SpecimenMaster" element={<ProtectedRoute><SpecimenMaster /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/MasterConfiguration/ResultTemplate/Add" element={<ProtectedRoute><AddResultTemplate /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/ClinicConfiguration/DemographicsLayout" element={<ProtectedRoute><DemographicsLayout /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/ClinicConfiguration/OutSourceConfiguration" element={<ProtectedRoute><OutSourceConfiguration /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/ClinicConfiguration/eSignConfiguration" element={<ProtectedRoute><ESignConfiguration /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/ClinicConfiguration/ReferredDoctor" element={<ProtectedRoute><ReferredDoctor /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/ClinicConfiguration/CustomConfiguration" element={<ProtectedRoute><CustomConfiguration /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/ClinicConfiguration/CorporateConfiguration" element={<ProtectedRoute><CorporateConfiguration /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/UserConfiguration/RoleAccess" element={<ProtectedRoute><RoleAccess /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/UserConfiguration/UserConfig" element={<ProtectedRoute><UserConfig /></ProtectedRoute>} />
      <Route path="/HistopathologySetting/UserConfiguration/RoleAccess/Add" element={<ProtectedRoute><AddRoleAccess /></ProtectedRoute>} />
      <Route path="/HistopathologyBilling" element={<Navigate to="/HistopathologyBilling/HospitalBilling" replace />} />
      <Route path="/HistopathologySpecimen" element={<Navigate to="/HistopathologySpecimen/SpecimenCollection" replace />} />
      <Route path="/HistopathologyReporting" element={<Navigate to="/HistopathologyReporting/PendingList" replace />} />
      <Route path="/HistopathologyReports" element={<Navigate to="/HistopathologyReports/OutsourceReport" replace />} />
      <Route path="/HistopathologyCreditSettlement" element={<Navigate to="/HistopathologyCreditSettlement/CorporateProvider" replace />} />
      <Route path="/HistopathologySetting/MasterConfiguration/" element={<Navigate to="/HistopathologySetting/MasterConfiguration/ServiceMaster" replace />} />
      <Route path="/HistopathologySetting/ClinicConfiguration/" element={<Navigate to="/HistopathologySetting/ClinicConfiguration/DemographicsLayout" replace />} />
      <Route path="/HistopathologySetting/UserConfiguration/" element={<Navigate to="/HistopathologySetting/UserConfiguration/RoleAccess" replace />} />

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}
